import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { API_BASE } from '../config/api';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const PAGE_SIZE = 31;

const emptyMetric = {
  offered: 0,
  answered: 0,
  answerDelaySeconds: 0,
  maxAnswerDelaySeconds: 0,
  avgAnswerDelaySeconds: 0,
  answeredAfterThreshold: 0,
  disconnected: 0,
  overflowed: 0,
  routed: 0,
  ivrTrans: 0,
  defaulted: 0,
  givenBusy: 0,
  abandoned: 0,
  abandonDelaySeconds: 0,
  maxAbandonDelaySeconds: 0,
  abandonedAfterThreshold: 0,
  serviceLevel: 0,
  talkTimeSeconds: 0,
};

const DEFAULT_SQL = `-- Primary source: call_state_log.route_point_number
WITH call_events AS (
  SELECT
    COALESCE(NULLIF(uniqueid, ''), NULLIF(channelid, ''), id::text) AS call_id,
    created_at::date AS report_date,
    NULLIF(route_point_number, '') AS dnis_number
  FROM call_state_log
  WHERE created_at >= :start_date::date
    AND created_at < (:end_date::date + INTERVAL '1 day')
    AND NULLIF(route_point_number, '') IS NOT NULL
)
SELECT report_date, dnis_number, COUNT(*) AS offered
FROM call_events
GROUP BY report_date, dnis_number
ORDER BY dnis_number ASC, report_date ASC;`;

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const monthStartIso = () => {
  const date = new Date();
  date.setDate(1);
  return toIsoDate(date);
};

const normalizeText = (value) => String(value ?? '').trim();

const formatDate = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const [year, month, day] = text.slice(0, 10).split('-');
  if (!year || !month || !day) return text;
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatNumber = (value) => Math.round(Number(value) || 0).toLocaleString();
const formatDecimal = (value) => (Number(value) || 0).toFixed(2);

const formatHms = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remSeconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remSeconds).padStart(2, '0')}`;
};

const metricCells = (metric = emptyMetric) => [
  formatNumber(metric.offered),
  formatNumber(metric.answered),
  formatHms(metric.answerDelaySeconds),
  formatHms(metric.maxAnswerDelaySeconds),
  formatHms(metric.avgAnswerDelaySeconds),
  formatNumber(metric.answeredAfterThreshold),
  formatNumber(metric.disconnected),
  formatNumber(metric.overflowed),
  formatNumber(metric.routed),
  formatNumber(metric.ivrTrans),
  formatNumber(metric.defaulted),
  formatNumber(metric.givenBusy),
  formatNumber(metric.abandoned),
  formatHms(metric.abandonDelaySeconds),
  formatHms(metric.maxAbandonDelaySeconds),
  formatNumber(metric.abandonedAfterThreshold),
  formatDecimal(metric.serviceLevel),
  formatHms(metric.talkTimeSeconds),
];

const dnisColumns = [
  'Offered',
  'Ans',
  'Ans Delay',
  'Max Ans Delay',
  'Avg Ans Delay',
  'Ans After Thld',
  'Disconnected',
  'Overflowed',
  'Routed',
  'IVR Trans',
  'Defaulted',
  'Given Busy',
  'Abnd',
  'Abnd Delay',
  'Max Abnd Delay',
  'Abnd After Thld',
  '% Service Level',
  'Talk Time',
];

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const normalizeMetric = (source = {}) => ({
  offered: Number(source.offered ?? source.Offered ?? 0) || 0,
  answered: Number(source.answered ?? source.Ans ?? source.Answered ?? 0) || 0,
  answerDelaySeconds: Number(source.answerDelaySeconds ?? source.answer_delay_seconds ?? 0) || 0,
  maxAnswerDelaySeconds: Number(source.maxAnswerDelaySeconds ?? source.max_answer_delay_seconds ?? 0) || 0,
  avgAnswerDelaySeconds: Number(source.avgAnswerDelaySeconds ?? source.avg_answer_delay_seconds ?? 0) || 0,
  answeredAfterThreshold: Number(source.answeredAfterThreshold ?? source.answered_after_threshold ?? 0) || 0,
  disconnected: Number(source.disconnected ?? 0) || 0,
  overflowed: Number(source.overflowed ?? 0) || 0,
  routed: Number(source.routed ?? 0) || 0,
  ivrTrans: Number(source.ivrTrans ?? source.ivr_trans ?? 0) || 0,
  defaulted: Number(source.defaulted ?? 0) || 0,
  givenBusy: Number(source.givenBusy ?? source.given_busy ?? 0) || 0,
  abandoned: Number(source.abandoned ?? source.Abnd ?? 0) || 0,
  abandonDelaySeconds: Number(source.abandonDelaySeconds ?? source.abandon_delay_seconds ?? 0) || 0,
  maxAbandonDelaySeconds: Number(source.maxAbandonDelaySeconds ?? source.max_abandon_delay_seconds ?? 0) || 0,
  abandonedAfterThreshold: Number(source.abandonedAfterThreshold ?? source.abandoned_after_threshold ?? 0) || 0,
  serviceLevel: Number(source.serviceLevel ?? source.service_level ?? 0) || 0,
  talkTimeSeconds: Number(source.talkTimeSeconds ?? source.talk_time_seconds ?? 0) || 0,
});

const normalizeSummary = (source = {}) => ({
  dnisNumber: normalizeText(source.dnisNumber ?? source.dnis_number ?? source.routePointNumber ?? source.route_point_number ?? source.RoutePointNumber ?? source.DnisNumber ?? ''),
  ...normalizeMetric(source),
});

const normalizeDetailRow = (source = {}) => ({
  date: normalizeText(source.date ?? source.reportDate ?? source.report_date ?? ''),
  dnisNumber: normalizeText(source.dnisNumber ?? source.dnis_number ?? source.routePointNumber ?? source.route_point_number ?? source.RoutePointNumber ?? source.DnisNumber ?? ''),
  ...normalizeMetric(source),
});

const buildReportPath = (filters, page = 1) => {
  const params = new URLSearchParams();
  params.set('startDate', filters.startDate);
  params.set('endDate', filters.endDate);
  params.set('page', String(page));
  params.set('perPage', String(PAGE_SIZE));
  if (filters.dnisNumber) params.set('dnisNumber', filters.dnisNumber);
  return `/reports/dnis-statistics?${params.toString()}`;
};

const fetchJson = async (path) => {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || json?.ok === false || json?.success === false) {
    throw new Error(json?.error || json?.message || text || `Request failed: ${response.status}`);
  }
  return json;
};

const DnisStatisticsReport = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState({
    startDate: monthStartIso(),
    endDate: todayIso(),
    dnisNumber: '',
  });
  const [report, setReport] = useState({
    totals: emptyMetric,
    dnisSummaries: [],
    detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
    reportInterval: '',
    siteName: 'CC',
    source: 'none',
    thresholdSeconds: 20,
    dataIssues: [],
    sqlOutput: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const dnisGroups = useMemo(() => (
    (report.dnisSummaries || []).map((summary) => ({
      summary,
      rows: (report.detail?.rows || []).filter((row) => row.dnisNumber === summary.dnisNumber),
    }))
  ), [report]);

  const loadReport = useCallback(async (nextFilters = filters, page = 1) => {
    if (!nextFilters.startDate || !nextFilters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (nextFilters.startDate > nextFilters.endDate) {
      setError('Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const json = await fetchJson(buildReportPath(nextFilters, page));
      const detail = json?.detail || json?.data?.detail || {};
      const rows = Array.isArray(detail.rows) ? detail.rows : [];
      const allRows = Array.isArray(detail.allRows) ? detail.allRows : rows;
      setReport({
        totals: normalizeMetric(json?.totals || json?.data?.totals || {}),
        dnisSummaries: (json?.dnisSummaries || json?.data?.dnisSummaries || []).map(normalizeSummary),
        detail: {
          rows: rows.map(normalizeDetailRow),
          allRows: allRows.map(normalizeDetailRow),
          page: Number(detail.page || page),
          perPage: Number(detail.perPage || PAGE_SIZE),
          totalRows: Number(detail.totalRows || allRows.length || rows.length),
          totalPages: Number(detail.totalPages || 1),
        },
        reportInterval: json?.reportInterval || json?.data?.reportInterval || '',
        siteName: normalizeText(json?.siteName || json?.data?.siteName || 'CC') || 'CC',
        source: normalizeText(json?.source || json?.data?.source || 'none'),
        thresholdSeconds: Number(json?.thresholdSeconds || json?.data?.thresholdSeconds || 20),
        dataIssues: Array.isArray(json?.dataIssues) ? json.dataIssues : [],
        sqlOutput: json?.sqlOutput || json?.data?.sqlOutput || '',
      });
    } catch (err) {
      setReport((prev) => ({
        ...prev,
        totals: emptyMetric,
        dnisSummaries: [],
        detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
      }));
      setError(err?.message || 'Failed to load DNIS report from PostgreSQL API.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  useEffect(() => {
    loadReport(filters, 1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value.trimStart() }));
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const summaryRows = [
      ['DNIS Statistics'],
      ['Report Interval', report.reportInterval || `${filters.startDate} - ${filters.endDate}`],
      ['Site Name', report.siteName],
      ['Data Source', report.source],
      ['Service Threshold Seconds', report.thresholdSeconds],
      [],
      ['Grand Total', ...dnisColumns],
      ['', ...metricCells(report.totals)],
      [],
      ['DNIS Summary'],
      ['DNIS Number', ...dnisColumns],
      ...(report.dnisSummaries || []).map((summary) => [summary.dnisNumber, ...metricCells(summary)]),
    ];
    const detailRows = [
      ['Date', 'DNIS Number', ...dnisColumns],
      ...(report.detail?.allRows || []).map((row) => [
        formatDate(row.date),
        row.dnisNumber,
        ...metricCells(row),
      ]),
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Daily Detail');
    XLSX.writeFile(workbook, `dnis-statistics-${filters.startDate}-${filters.endDate}.xlsx`);
  };

  const currentPage = Number(report.detail?.page || 1);
  const totalPages = Number(report.detail?.totalPages || 1);
  const totalRows = Number(report.detail?.totalRows || 0);

  return (
    <div className="cdn-report-shell dnis-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>DNIS Statistics</h1>
          <p>PostgreSQL MIS report</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports')}>CDN Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/daily-call-report')}>Daily Call Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-state-report')}>Agent State</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-login-logout-report')}>Login/Logout</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-performance-report')}>Performance</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="dnis-filter-grid">
          <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>
          <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>
          <label><span>DNIS Number</span><input type="text" name="dnisNumber" value={filters.dnisNumber} onChange={updateField} placeholder="All DNIS" /></label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={() => loadReport(filters, 1)} disabled={loading}>
              {loading ? 'Loading' : 'Show'}
            </button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!report.detail?.allRows?.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!report.detail?.allRows?.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
        {report.dataIssues?.length ? (
          <div className="dnis-data-issues">
            {report.dataIssues.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}
      </section>

      <main className="cdn-report-paper dnis-report-paper">
        <section className="cdn-report-heading">
          <h2>DNIS Statistics</h2>
          <p><strong>Report Interval:</strong> {report.reportInterval || `${filters.startDate} 00:00:00 - ${filters.endDate} 23:59:59`}</p>
          <p><strong>Site Name:</strong> {report.siteName}</p>
        </section>

        <section className="dnis-grand-total">
          <table className="dnis-report-table">
            <thead>
              <tr>
                <th></th>
                {dnisColumns.map((column) => <th key={column}>{column}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr className="cdn-total-row">
                <td>Grand Total</td>
                {metricCells(report.totals).map((cell, index) => <td key={index}>{cell}</td>)}
              </tr>
            </tbody>
          </table>
        </section>

        {dnisGroups.length === 0 ? (
          <div className="cdn-empty">{loading ? 'Loading report...' : 'No report data found.'}</div>
        ) : dnisGroups.map(({ summary, rows }) => (
          <section className="cdn-route-section dnis-section" key={summary.dnisNumber}>
            <h3>DNIS Number: {summary.dnisNumber}</h3>
            <table className="dnis-report-table">
              <thead>
                <tr className="cdn-summary-row">
                  <th>Summary:</th>
                  {metricCells(summary).map((cell, index) => <th key={index}>{cell}</th>)}
                </tr>
                <tr>
                  <th>Date</th>
                  {dnisColumns.map((column) => <th key={column}>{column}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.dnisNumber}-${row.date}`}>
                    <td>{formatDate(row.date)}</td>
                    {metricCells(row).map((cell, index) => <td key={index}>{cell}</td>)}
                  </tr>
                ))}
                <tr className="cdn-total-row">
                  <td>DNIS: {summary.dnisNumber}</td>
                  {metricCells(summary).map((cell, index) => <td key={index}>{cell}</td>)}
                </tr>
              </tbody>
            </table>
          </section>
        ))}

        {dnisGroups.length > 0 ? (
          <section className="dnis-bottom-total">
            <table className="dnis-report-table">
              <tbody>
                <tr className="cdn-total-row">
                  <td>Grand Total</td>
                  {metricCells(report.totals).map((cell, index) => <td key={index}>{cell}</td>)}
                </tr>
              </tbody>
            </table>
          </section>
        ) : null}
      </main>

      <section className="cdn-pager no-print">
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage <= 1} onClick={() => loadReport(filters, currentPage - 1)}>Previous</button>
        <span>Page {currentPage} of {totalPages} - {formatNumber(totalRows)} daily rows</span>
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage >= totalPages} onClick={() => loadReport(filters, currentPage + 1)}>Next</button>
      </section>

      <section className="cdn-sql-panel no-print">
        <details>
          <summary>PostgreSQL SQL Output</summary>
          <pre>{report.sqlOutput || DEFAULT_SQL}</pre>
        </details>
      </section>
    </div>
  );
};

export default DnisStatisticsReport;
